import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { PshipPtrService } from './pship-ptr.service';

@Component({
    selector: 'gtw-pship-ptr',
    templateUrl: './pship-ptr.component.html',
    styleUrls: ['./pship-ptr.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class PshipPtrComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  private readonly subs = new Subscription();
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // Standard component properties
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = false;
  
  // Modal and form properties
  modalTitle = "Special Allocation";
  
  // No entity table needed for this component
  
  // Component specific data
  mode: 'normal' | 'beat' = 'normal'; // Mode: normal or beat
  showAmounts: boolean = true; // Whether to show Amount columns (false for Update Special Allocation)
  pshipLeid: any;
  pshipCdrNo: any;
  pshipReportingPeriod: any;
  pshipMeCode: any;
  pshipAmount: any;
  pshipCCKey: any;
  pshipTagKey: any;
  pshipGieKey: any; // Used in BEAT mode
  pshipBeatPaymentAmount: any; // BEAT mode
  pshipBeatBenefitAmount: any; // BEAT mode
  
  partnerDetailsData: any[] = [];
  total_allocation_ratio = 0;
  total_allocation_amount = 0;
  totalActualRatio = 0;
  ptr_actual_ratio = 0;
  ptr_actual_amount = 0;
  ptr_special_actual_ratio = 0;
  ptr_special_actual_amount = 0;
  
  // BEAT mode specific totals
  total_allocation_beat_payment = 0;
  total_allocation_beat_benefit = 0;
  ptr_actual_beat_payment = 0;
  ptr_actual_beat_benefit = 0;
  ptr_special_actual_beat_payment = 0;
  ptr_special_actual_beat_benefit = 0;
  
  // Global data
  globalService: any;
  userSettings: any;
  generalConfig: any;
  filterParams: any = {};
  rowData: any = {};
  baseUrls: any;
  
  // Dependencies injection simulation
  logged_in_user: string = '';
  curentyear: any;
  scenario: any;
  jcd_key: any;

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private pshipPtrService: PshipPtrService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseUrls = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = { ...(header.globalParams || {}) };
    this.userSettings = header.userSettingsData || {};
    this.generalConfig = header.generalConfigData || {};
    this.logged_in_user = this.userSettings?.user?.sso_id || '';
    this.jcd_key = this.globalService?.jcd_key;
    this.filterParams = normalizeFilterParams(activeScreen);
    this.curentyear = this.filterParams.tax_year;
    this.scenario = this.filterParams.scenario;
    const m = activeScreen?.data?.mode || '';
    this.mode = (m === 'beat' || m === 'normal') ? m : 'normal';
    const sa = activeScreen?.data?.showAmounts ?? activeScreen?.data?.show_amounts ?? '';
    const sas = sa === true || sa === false ? String(sa) : String(sa ?? '');
    this.showAmounts = !(sas === 'false' || sas === '0');
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    let extractedData: any = Array.isArray(rawRow) ? (rawRow[0] ?? {}) : rawRow;
    extractedData = extractedData?.rowData ?? extractedData;
    if (Array.isArray(extractedData) && extractedData.length > 0) {
      extractedData = extractedData[0];
    }
    if (extractedData?.data && typeof extractedData.data === 'object') {
      this.rowData = extractedData.data;
    } else {
      this.rowData = extractedData || {};
    }
  }

  init() {
    try {
      if (this.rowData && Object.keys(this.rowData).length > 0 && 
          this.filterParams && Object.keys(this.filterParams).length > 0 &&
          this.generalConfig) {
        // Small delay to ensure all data is properly set
        setTimeout(() => {
          this.setupComponentData();
          this.checkLockStatus();
        }, 100);
      }
    } catch (e) {
      console.error('Error in pshipPtr init:', e);
    }
  }

  private setupComponentData() {
    // Extract partnership data from rowData
    console.log(`Setting up component data (${this.mode} mode) from rowData:`, this.rowData);
    console.log('Available rowData keys:', Object.keys(this.rowData || {}));
    console.log('rowData values:', this.rowData);
    
    // Common fields
    this.pshipLeid = this.rowData?.PTRSHIP_LEID || this.rowData?.LEID;
    this.pshipCdrNo = this.rowData?.PTRSHIP_CDR_NO || this.rowData?.CDR_NO;
    this.pshipReportingPeriod = this.rowData?.PTRSHIP_REPORTING_PERIOD || this.rowData?.REPORTING_PERIOD;
    this.pshipMeCode = this.rowData?.PTRSHIP_ME_CODE || this.rowData?.ME_CODE;
    this.pshipCCKey = this.rowData?.PTRSHIP_COMBINATION_KEY || this.rowData?.COMBINATION_KEY;
    
    console.log('After extraction:', {
      pshipLeid: this.pshipLeid,
      pshipCdrNo: this.pshipCdrNo,
      pshipReportingPeriod: this.pshipReportingPeriod,
      pshipMeCode: this.pshipMeCode,
      pshipCCKey: this.pshipCCKey
    });
    
    // Mode-specific fields
    if (this.mode === 'beat') {
      // BEAT mode uses GIE_KEY instead of TAG_KEY
      this.pshipGieKey = this.rowData?.GIE_KEY;
      console.log('BEAT mode - Partnership data set:', {
        pshipLeid: this.pshipLeid,
        pshipCdrNo: this.pshipCdrNo,
        pshipReportingPeriod: this.pshipReportingPeriod,
        pshipMeCode: this.pshipMeCode,
        pshipCCKey: this.pshipCCKey,
        pshipGieKey: this.pshipGieKey
      });
    } else {
      // Normal mode uses TAG_KEY and Amount
      this.pshipAmount = this.rowData?.PTRSHIP_AMT || this.rowData?.AMOUNT;
      this.pshipTagKey = this.rowData?.PTRSHIP_TAG_KEY || this.rowData?.TAG_KEY;
      console.log('Normal mode - Partnership data set:', {
        pshipLeid: this.pshipLeid,
        pshipCdrNo: this.pshipCdrNo,
        pshipReportingPeriod: this.pshipReportingPeriod,
        pshipMeCode: this.pshipMeCode,
        pshipAmount: this.pshipAmount,
        pshipCCKey: this.pshipCCKey,
        pshipTagKey: this.pshipTagKey
      });
    }
    
    // Check required data based on mode
    const hasRequiredData = this.mode === 'beat' 
      ? (this.pshipCCKey && this.pshipGieKey && this.scenario && this.jcd_key)
      : (this.pshipCCKey && this.pshipTagKey && this.scenario && this.jcd_key);
    
    if (hasRequiredData) {
      this.getPartnerDetails();
    } else {
      console.warn(`Missing required data for API call (${this.mode} mode):`, {
        pshipCCKey: this.pshipCCKey,
        pshipTagKey: this.pshipTagKey,
        pshipGieKey: this.pshipGieKey,
        scenario: this.scenario,
        jcd_key: this.jcd_key
      });
    }
  }

  private checkLockStatus() {
    // Check lock status
    const lockparams = {
      "action_code": 'n10cpv',
      'p_scenario': this.scenario,
      'p_jcd_key': this.jcd_key
    };

    this.pshipPtrService.checkLockStatus(
      this.generalConfig.base_url,
      lockparams,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        console.log('Lock status response:', response);
        const lockData = response.jsonObject || response || [];
        if (lockData && lockData.length > 0 && lockData[0].LOCK_STATUS === 'Y') {
          this.cancel();
          this._snackBar.open(
            `Tax Return has already been filed for tax year ${this.filterParams.tax_year}.`, 
            'Close', 
            { 
              duration: 4000, 
              panelClass: ['error-snackbar'] 
            }
          );
        }
      },
      (error: any) => {
        console.error('Error checking lock status:', error);
      }
    );
  }

  getPartnerDetails() {
    let params: any;
    
    if (this.mode === 'beat') {
      // BEAT mode uses different action code and GIE_KEY
      params = {
        "action_code": 'n9qym0',
        "p_ptrship_cc_key": this.pshipCCKey,
        "p_ptrship_gie_key": this.pshipGieKey,
        'p_scenario': this.scenario,
        'p_jcd_key': this.jcd_key
      };
    } else {
      // Normal mode uses TAG_KEY
      params = {
        "action_code": 'qxwr80',
        "p_ptrship_cc_key": this.pshipCCKey,
        "p_ptrship_tag_key": this.pshipTagKey,
        'p_scenario': this.scenario,
        'p_jcd_key': this.jcd_key
      };
    }

    console.log(`Getting partner details (${this.mode} mode) with params:`, params);

    this.showFormLoader = true;
    this.pshipPtrService.getPartnerDetails(
      this.generalConfig.base_url,
      params,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.showFormLoader = false;
        console.log('Partner details response:', response);
        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this.partnerDetailsData = response.jsonObject || response || [];
          console.log('Partner details data:', this.partnerDetailsData);
          this.calculateTotals();
        }
      },
      (error: any) => {
        this.showFormLoader = false;
        this._snackBar.open('Error loading partner details. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        console.error('Error loading partner details:', error);
      }
    );
  }

  private calculateTotals() {
    this.totalActualRatio = 0;
    this.ptr_actual_ratio = 0;
    this.ptr_actual_amount = 0;
    this.ptr_special_actual_ratio = 0;
    this.ptr_special_actual_amount = 0;
    
    // BEAT mode totals
    this.ptr_actual_beat_payment = 0;
    this.ptr_actual_beat_benefit = 0;
    this.ptr_special_actual_beat_payment = 0;
    this.ptr_special_actual_beat_benefit = 0;

    this.partnerDetailsData.forEach((record: any) => {
      // Initialize NEW_ALLOCATION fields
      if (record.NEW_ALLOCATION_RATIO === undefined || record.NEW_ALLOCATION_RATIO === null) {
        record.NEW_ALLOCATION_RATIO = 0;
      }
      
      // Common totals
      this.totalActualRatio += record.PTR_ACTUAL_RATIO || 0;
      this.ptr_actual_ratio += record.PTR_ACTUAL_RATIO || 0;
      this.ptr_special_actual_ratio += record.PTR_SPECIAL_ALLOCATION_RATIO || 0;
      
      if (this.mode === 'beat') {
        // BEAT mode: calculate beat payment/benefit
        if (record.NEW_ALLOCATION_BEAT_PAYMENT === undefined || record.NEW_ALLOCATION_BEAT_PAYMENT === null) {
          record.NEW_ALLOCATION_BEAT_PAYMENT = 0;
        }
        if (record.NEW_ALLOCATION_BEAT_BENEFIT === undefined || record.NEW_ALLOCATION_BEAT_BENEFIT === null) {
          record.NEW_ALLOCATION_BEAT_BENEFIT = 0;
        }
        
        this.ptr_actual_beat_payment += record.PTR_ACTUAL_BEAT_PAYMENT || 0;
        this.ptr_actual_beat_benefit += record.PTR_ACTUAL_BEAT_BENEFIT || 0;
        this.ptr_special_actual_beat_payment += record.PTR_SPECIAL_ALLOCATION_BEAT_PAYMENT || 0;
        this.ptr_special_actual_beat_benefit += record.PTR_SPECIAL_ALLOCATION_BEAT_BENEFIT || 0;
        
        // Get BEAT amounts from first record
        this.pshipBeatPaymentAmount = record.PSHIP_BEAT_PAYMENT;
        this.pshipBeatBenefitAmount = record.PSHIP_BEAT_BENEFIT;
      } else {
        // Normal mode: calculate amount
        if (record.NEW_ALLOCATION_AMOUNT === undefined || record.NEW_ALLOCATION_AMOUNT === null) {
          record.NEW_ALLOCATION_AMOUNT = 0;
        }
        
        this.pshipAmount = record.PSHIP_AMT || this.pshipAmount;
        this.ptr_actual_amount += record.PTR_ACTUAL_AMOUNT || 0;
        this.ptr_special_actual_amount += record.PTR_SPECIAL_ALLOCATION_AMOUNT || 0;
      }
    });
    
    console.log(`Calculated totals (${this.mode} mode):`, {
      totalActualRatio: this.totalActualRatio,
      ptr_actual_ratio: this.ptr_actual_ratio,
      ...(this.mode === 'beat' ? {
        ptr_actual_beat_payment: this.ptr_actual_beat_payment,
        ptr_actual_beat_benefit: this.ptr_actual_beat_benefit
      } : {
        ptr_actual_amount: this.ptr_actual_amount
      })
    });
  }

  updateAllocationAmount(data: any) {
    console.log(`Updating allocation amount (${this.mode} mode) for:`, data);
    
    // Limit decimal places
    if (data.NEW_ALLOCATION_RATIO && data.NEW_ALLOCATION_RATIO.toString().length > 12) {
      data.NEW_ALLOCATION_RATIO = parseFloat(data.NEW_ALLOCATION_RATIO).toFixed(10);
    }

    // Limit maximum value
    if (data.NEW_ALLOCATION_RATIO > 2) {
      data.NEW_ALLOCATION_RATIO = 1;
    }

    this.total_allocation_ratio = 0;
    
    const ratio = (data.NEW_ALLOCATION_RATIO == null || data.NEW_ALLOCATION_RATIO === '') ? 0 : data.NEW_ALLOCATION_RATIO;
    
    if (this.mode === 'beat') {
      // BEAT mode: calculate beat payment and benefit
      this.total_allocation_beat_payment = 0;
      this.total_allocation_beat_benefit = 0;
      
      const beatPaymentAmt = (this.pshipBeatPaymentAmount === '' || this.pshipBeatPaymentAmount == null) ? 0 : this.pshipBeatPaymentAmount;
      const beatBenefitAmt = (this.pshipBeatBenefitAmount === '' || this.pshipBeatBenefitAmount == null) ? 0 : this.pshipBeatBenefitAmount;
      
      data.NEW_ALLOCATION_BEAT_PAYMENT = beatPaymentAmt * parseFloat(ratio);
      data.NEW_ALLOCATION_BEAT_BENEFIT = beatBenefitAmt * parseFloat(ratio);
      
      // Recalculate totals for all records
      this.partnerDetailsData.forEach((record: any) => {
        const recordRatio = (record.NEW_ALLOCATION_RATIO === '' || record.NEW_ALLOCATION_RATIO == null) ? 0 : record.NEW_ALLOCATION_RATIO;
        this.total_allocation_ratio += parseFloat(recordRatio);
        
        record.NEW_ALLOCATION_BEAT_PAYMENT = beatPaymentAmt * parseFloat(recordRatio);
        record.NEW_ALLOCATION_BEAT_BENEFIT = beatBenefitAmt * parseFloat(recordRatio);
        
        this.total_allocation_beat_payment += parseFloat(record.NEW_ALLOCATION_BEAT_PAYMENT || 0);
        this.total_allocation_beat_benefit += parseFloat(record.NEW_ALLOCATION_BEAT_BENEFIT || 0);
      });
    } else {
      // Normal mode: calculate amount
      this.total_allocation_amount = 0;
      
      const pshipAmt = (this.pshipAmount === '' || this.pshipAmount == null) ? 0 : this.pshipAmount;
      data.NEW_ALLOCATION_AMOUNT = pshipAmt * parseFloat(ratio);
      
      // Recalculate totals for all records
      this.partnerDetailsData.forEach((record: any) => {
        const recordRatio = (record.NEW_ALLOCATION_RATIO === '' || record.NEW_ALLOCATION_RATIO == null) ? 0 : record.NEW_ALLOCATION_RATIO;
        this.total_allocation_ratio += parseFloat(recordRatio);
        
        record.NEW_ALLOCATION_AMOUNT = pshipAmt * parseFloat(recordRatio);
        this.total_allocation_amount += parseFloat(record.NEW_ALLOCATION_AMOUNT || 0);
      });
    }
    
    console.log(`Updated totals (${this.mode} mode):`, {
      total_allocation_ratio: this.total_allocation_ratio,
      ...(this.mode === 'beat' ? {
        total_allocation_beat_payment: this.total_allocation_beat_payment,
        total_allocation_beat_benefit: this.total_allocation_beat_benefit
      } : {
        total_allocation_amount: this.total_allocation_amount
      })
    });
  }

  save() {
    this.isSaveClicked = true;
    this.crudLoading = true;

    // Validate allocation ratio
    if (this.total_allocation_ratio !== this.totalActualRatio) {
      this.isSaveClicked = false;
      this.crudLoading = false;
      this._snackBar.open('The Special Allocation ratio should be equal to Total Actual Ratio.', 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    // Prepare save data based on mode
    const allPshipPtrDetails: any[] = [];
    
    if (this.mode === 'beat') {
      // BEAT mode: use different keys
      this.partnerDetailsData.forEach((partner: any) => {
        const returnObj = {
          "pship_cc_key": this.pshipCCKey,
          "pship_gie_key": this.pshipGieKey,
          'ptr_cc_key': partner.PTR_COMBINATION_KEY,
          'special_allocation_ratio': partner.NEW_ALLOCATION_RATIO
        };
        allPshipPtrDetails.push(returnObj);
      });
    } else {
      // Normal mode: use tag keys
      this.partnerDetailsData.forEach((partner: any) => {
        const returnObj = {
          "pship_tag_key": this.pshipTagKey,
          "ptr_tag_key": partner.PTR_TAG_KEY,
          'special_allocation_ratio': partner.NEW_ALLOCATION_RATIO
        };
        allPshipPtrDetails.push(returnObj);
      });
    }

    const returnClobSettingsObj = {
      'sso_id': this.logged_in_user,
      'screen_key': this.globalService?.screen_key,
      'p_tax_year': this.curentyear,
      'p_scenario': this.scenario,
      'p_jcd_key': this.jcd_key
    };

    console.log(`Saving (${this.mode} mode) with data:`, {
      allPshipPtrDetails,
      returnClobSettingsObj
    });
    
    // Use appropriate service method based on mode
    const saveObservable = this.mode === 'beat' 
      ? this.pshipPtrService.saveBeatPtrRatio(
          this.generalConfig.base_url,
          allPshipPtrDetails,
          returnClobSettingsObj,
          this.globalService,
          this.filterParams,
          this.rowData
        )
      : this.pshipPtrService.savePshipPtrForm(
          this.generalConfig.base_url,
          allPshipPtrDetails,
          returnClobSettingsObj,
          this.globalService,
          this.filterParams,
          this.rowData
        );
    
    saveObservable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        
        console.log('Save response:', response);

        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('Data has been successfully saved/updated', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          // CRITICAL: Emit 'refresh' string for AngularJS modal close
          this.requestGridUpdate.emit('refresh');
        }
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        console.error('Error saving partnership pointer data:', error);
      }
    );
  }

  reset() {
    this.partnerDetailsData.forEach((record: any) => {
      record.NEW_ALLOCATION_RATIO = 0;
      if (this.mode === 'beat') {
        record.NEW_ALLOCATION_BEAT_PAYMENT = 0;
        record.NEW_ALLOCATION_BEAT_BENEFIT = 0;
      } else {
        record.NEW_ALLOCATION_AMOUNT = 0;
      }
    });
    this.total_allocation_ratio = 0;
    if (this.mode === 'beat') {
      this.total_allocation_beat_payment = 0;
      this.total_allocation_beat_benefit = 0;
    } else {
      this.total_allocation_amount = 0;
    }
  }

  cancel() {
    // CRITICAL: Use EventEmitter for AngularJS modal dismissal
    this.requestGridUpdate.emit('cancel');
  }
}
